import React, { useState } from 'react';
import type { DataPreviewRow } from './types';

interface ProseDocumentSection {
  section_id: string;
  heading: string;
  body: string;
}

interface ProseDocument {
  filename: string;
  sections: ProseDocumentSection[];
}

interface DataPreviewTabProps {
  previewBefore: string;
  previewAfter: string;
  rows: DataPreviewRow[];
  stats: Record<string, any>;
  proseDocuments?: ProseDocument[] | null;
}

type ViewMode = 'before' | 'after' | 'changes';

const ProseDocumentView: React.FC<{ documents: ProseDocument[] }> = ({ documents }) => {
  const [activeDocIdx, setActiveDocIdx] = useState(0);
  if (!documents || documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm font-semibold text-[#0F172A]">No document content available</p>
        <p className="text-xs text-[#94A3B8] mt-1">The reconciled document state could not be loaded.</p>
      </div>
    );
  }
  const safeIdx = Math.min(activeDocIdx, documents.length - 1);
  const activeDoc = documents[safeIdx];
  const totalSections = activeDoc?.sections?.length || 0;
  return (
    <div className="space-y-4">
      {documents.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] flex-shrink-0">Document</span>
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-thin pb-1 -mb-1 flex-1">
            {documents.map((doc, i) => (
              <button
                key={doc.filename + i}
                onClick={() => setActiveDocIdx(i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium border transition-colors whitespace-nowrap flex-shrink-0 ${
                  i === safeIdx
                    ? 'bg-[#0D9488] text-white border-[#0D9488]'
                    : 'bg-white text-[#475569] border-[#E2E8F0] hover:bg-[#F1F5F9]'
                }`}
              >
                <span className="truncate max-w-[260px]">{doc.filename || `Document ${i + 1}`}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${i === safeIdx ? 'bg-white/25 text-white' : 'bg-[#F1F5F9] text-[#475569]'}`}>{doc.sections?.length || 0}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-2 text-[12px] text-[#475569]">
        <span className="font-semibold">{activeDoc.filename}</span>
        <span className="ml-2 text-[#94A3B8]">{totalSections} section{totalSections === 1 ? '' : 's'}</span>
      </div>
      {totalSections === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-[#E2E8F0] rounded-lg">
          <p className="text-sm text-[#64748B]">This document has no sections.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeDoc.sections.map((section, idx) => (
            <article key={section.section_id || idx} className="bg-white border border-[#E2E8F0] rounded-lg px-5 py-4">
              {section.heading ? (
                <h3 className="text-[14px] font-semibold text-[#0F172A] mb-2">{section.heading}</h3>
              ) : (
                <h3 className="text-[12px] uppercase tracking-wider font-semibold text-[#94A3B8] mb-2">Section {idx + 1}</h3>
              )}
              {section.body
                ? section.body.split(/\n{2,}/).map((para, pi) => para.trim() && (
                    <p key={pi} className="text-[13px] text-[#334155] leading-relaxed mb-2 last:mb-0 whitespace-pre-wrap">{para.trim()}</p>
                  ))
                : <p className="text-[12px] italic text-[#94A3B8]">(empty section)</p>}
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

const DataPreviewTab: React.FC<DataPreviewTabProps> = ({ previewBefore, previewAfter, rows, stats, proseDocuments }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('after');
  const [page, setPage] = useState(0);
  const pageSize = 10;

  if (proseDocuments && proseDocuments.length > 0) {
    return <ProseDocumentView documents={proseDocuments} />;
  }

  const changedCount = rows.filter(r => r.status === 'changed').length;
  const flaggedCount = rows.filter(r => r.status === 'flagged').length;
  const totalCount = rows.length;

  const filteredRows = viewMode === 'changes'
    ? rows.filter(r => r.status === 'changed' || r.status === 'flagged')
    : rows;
  const totalPages = Math.ceil(filteredRows.length / pageSize);
  const pageRows = filteredRows.slice(page * pageSize, (page + 1) * pageSize);
  const columns = pageRows.length > 0 ? pageRows[0].cells.map(c => c.column) : [];

  const hasStructuredData = rows.length > 0;
  const hasMarkdownPreview = previewBefore || previewAfter;

  if (!hasStructuredData && !hasMarkdownPreview) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm font-semibold text-[#0F172A]">No preview available</p>
        <p className="text-xs text-[#94A3B8] mt-1">Data preview will appear after cleaning is complete.</p>
      </div>
    );
  }

  if (!hasStructuredData && hasMarkdownPreview) {
    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('before')}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${viewMode === 'before' ? 'bg-[#0D9488] text-white' : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'}`}
          >
            Before Cleaning
          </button>
          <button
            onClick={() => setViewMode('after')}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${viewMode === 'after' ? 'bg-[#0D9488] text-white' : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'}`}
          >
            After Cleaning
          </button>
        </div>
        <MarkdownTable content={viewMode === 'before' ? previewBefore : previewAfter} />
      </div>
    );
  }

  const totalRows = stats?.totalRows;
  const rowsAfter = stats?.rowsAfterCleaning ?? totalCount;
  const rowsRemoved = typeof totalRows === 'number' && typeof rowsAfter === 'number' && totalRows > rowsAfter
    ? totalRows - rowsAfter
    : 0;
  const hasCellAudit = changedCount > 0 || flaggedCount > 0;

  return (
    <div className="space-y-4">
      {hasCellAudit ? (
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-2 mb-2 text-[12px] text-[#0F172A]">
          <span className="font-semibold">{changedCount} cells changed</span> across {rows.filter(r => r.status === 'changed').length} rows &middot; <span className="font-semibold">{flaggedCount} rows flagged</span> for review &middot; {totalCount - changedCount - flaggedCount} rows unchanged
        </div>
      ) : (
        <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg px-4 py-2 mb-2 text-[12px] text-[#1E3A8A]">
          Showing the cleaned dataset ({totalCount} rows{rowsRemoved > 0 ? `, ${rowsRemoved} removed during cleaning` : ''}). Per-cell change details are recorded once you apply a fix from the Questions tab — see Change Log for what cleaning already did.
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {(['before', 'after', 'changes'] as ViewMode[]).map(mode => (
            <button
              key={mode}
              onClick={() => { setViewMode(mode); setPage(0); }}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${
                viewMode === mode ? 'bg-[#0D9488] text-white' : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'
              }`}
            >
              {mode === 'before' ? 'Before Cleaning' : mode === 'after' ? 'After Cleaning' : 'Show Changes'}
            </button>
          ))}
        </div>
        <div className="flex gap-3 text-[12px]">
          {changedCount > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#DCFCE7] border border-[#A7F3D0]" />
              <span className="text-[#047857] font-semibold">{changedCount} changed</span>
            </span>
          )}
          {flaggedCount > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#FEF3C7] border border-[#FDE68A]" />
              <span className="text-[#92400E] font-semibold">{flaggedCount} flagged</span>
            </span>
          )}
          <span className="text-[#94A3B8]">{totalCount} total</span>
        </div>
      </div>

      {viewMode === 'changes' && changedCount === 0 && flaggedCount === 0 && (
        <div className="text-center py-8 text-[13px] text-[#94A3B8]">No changes to display</div>
      )}

      {pageRows.length > 0 && (
        <>
          <div className="border border-[#E2E8F0] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-[#1E293B]">
                    <th className="px-3 py-2.5 text-left font-semibold text-white text-[10px] uppercase tracking-wider sticky left-0 bg-[#1E293B]">Row</th>
                    {columns.map(col => (
                      <th key={col} className="px-3 py-2.5 text-left font-semibold text-white text-[10px] uppercase tracking-wider whitespace-nowrap">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, ri) => (
                    <tr key={ri} className={`border-t border-[#F1F5F9] ${
                      row.status === 'removed' ? 'bg-[#FEE2E2] opacity-70' :
                      ri % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'
                    }`}>
                      <td className="px-3 py-2 font-mono text-[#64748B] sticky left-0 bg-inherit">
                        <div className="flex items-center gap-1.5">
                          {row.status === 'changed' && <span className="text-[9px] bg-[#DCFCE7] text-[#047857] px-1 py-0.5 rounded font-bold">{'\u270E'}</span>}
                          {row.status === 'flagged' && <span className="text-[9px] bg-[#FEF3C7] text-[#92400E] px-1 py-0.5 rounded font-bold">!</span>}
                          {row.status === 'removed' && <span className="text-[9px] bg-[#FEE2E2] text-[#991B1B] px-1 py-0.5 rounded font-bold">&mdash;</span>}
                          {row.row_number}
                        </div>
                      </td>
                      {row.cells.map((cell, ci) => (
                        <td
                          key={ci}
                          title={cell.changed ? `Changed: "${cell.original_value}" \u2192 "${cell.current_value}"` : cell.flagged ? `Flagged: ${cell.current_value || 'NULL'}` : undefined}
                          className={`px-3 py-2 font-mono whitespace-nowrap cursor-default ${
                            cell.changed ? 'bg-[#DCFCE7] border-l-2 border-[#A7F3D0]' :
                            cell.flagged ? 'bg-[#FEF3C7] border-l-2 border-[#FDE68A]' : ''
                          }`}
                        >
                          {cell.changed && viewMode === 'changes' ? (
                            <span>
                              <span className="line-through text-[#94A3B8] mr-1">{cell.original_value}</span>
                              <span className="text-[#047857] font-semibold">{cell.current_value}</span>
                            </span>
                          ) : (
                            <span className={cell.changed ? 'text-[#047857] font-semibold' : cell.flagged ? 'text-[#92400E] italic' : 'text-[#334155]'}>
                              {(viewMode === 'before' ? cell.original_value : cell.current_value) || <span className="text-[#94A3B8] italic">NULL</span>}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={`w-7 h-7 rounded text-[11px] font-semibold transition-colors ${
                    page === i ? 'bg-[#0D9488] text-white' : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              {totalPages > 5 && (
                <>
                  <span className="text-[#94A3B8] self-center px-1">...</span>
                  <button
                    onClick={() => setPage(totalPages - 1)}
                    className={`w-7 h-7 rounded text-[11px] font-semibold ${page === totalPages - 1 ? 'bg-[#0D9488] text-white' : 'bg-[#F1F5F9] text-[#64748B]'}`}
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>
            <span className="text-[11px] text-[#94A3B8]">{pageSize} per page</span>
          </div>
        </>
      )}
    </div>
  );
};

function isEmptyValue(value: string): boolean {
  const normalized = (value || '').trim().toLowerCase();
  return normalized === '' || normalized === 'nan' || normalized === 'none' || normalized === 'null' || normalized === 'nat' || normalized === 'n/a' || normalized === 'na' || normalized === 'undefined';
}

function isGenericHeader(header: string): boolean {
  const trimmed = (header || '').trim();
  if (!trimmed) return true;
  if (/^unnamed[:\s_-]*\d*$/i.test(trimmed)) return true;
  if (/^\d+$/.test(trimmed)) return true;
  if (/^column[\s_-]*\d+$/i.test(trimmed)) return true;
  return false;
}

function looksLikeHeaderRow(row: string[]): boolean {
  const nonEmpty = row.filter(c => !isEmptyValue(c));
  if (nonEmpty.length < 2) return false;
  const headerLike = nonEmpty.filter(c => {
    const t = c.trim();
    if (t.length > 50) return false;
    if (/^\d+(\.\d+)?$/.test(t)) return false;
    return /[A-Za-z]/.test(t);
  });
  return headerLike.length >= Math.max(2, Math.floor(nonEmpty.length * 0.6));
}

function formatCellValue(value: string): { display: string; isEmpty: boolean } {
  if (isEmptyValue(value)) return { display: '\u2014', isEmpty: true };
  return { display: value, isEmpty: false };
}

function promoteHeadersIfNeeded(rawHeaders: string[], dataRows: string[][]): { headers: string[]; rows: string[][] } {
  const genericCount = rawHeaders.filter(isGenericHeader).length;
  const allGeneric = genericCount === rawHeaders.length;
  const mostlyGeneric = genericCount >= Math.max(2, Math.floor(rawHeaders.length * 0.7));

  if (!allGeneric && !mostlyGeneric) {
    return { headers: rawHeaders, rows: dataRows };
  }

  for (let i = 0; i < Math.min(dataRows.length, 10); i++) {
    const candidate = dataRows[i];
    if (looksLikeHeaderRow(candidate)) {
      const newHeaders = candidate.map((c, idx) => {
        const cleaned = (c || '').trim();
        if (!cleaned || isEmptyValue(cleaned)) {
          const existing = rawHeaders[idx];
          if (existing && !isGenericHeader(existing)) return existing;
          return `Column ${String.fromCharCode(65 + idx)}`;
        }
        return cleaned;
      });
      return { headers: newHeaders, rows: dataRows.slice(i + 1) };
    }
  }

  return {
    headers: rawHeaders.map((h, i) => isGenericHeader(h) ? `Column ${String.fromCharCode(65 + i)}` : h),
    rows: dataRows,
  };
}

const MarkdownTable: React.FC<{ content: string }> = ({ content }) => {
  if (!content) return <p className="text-[13px] text-[#94A3B8] italic py-4">No preview available</p>;

  const lines = content.split('\n').filter(l => l.trim() && !/^\|[\s\-|:]+\|$/.test(l.trim()));
  if (lines.length === 0) return <p className="text-[13px] text-[#94A3B8] italic py-4">No data</p>;

  const parseRow = (line: string) => line.replace(/^\||\|$/g, '').split('|').map(c => c.trim());
  const rawHeaders = parseRow(lines[0]);
  const rawDataRows = lines.slice(1).map(parseRow);

  const { headers, rows: promotedRows } = promoteHeadersIfNeeded(rawHeaders, rawDataRows);

  const nonEmptyRows = promotedRows.filter(row => row.some(cell => !isEmptyValue(cell)));

  if (nonEmptyRows.length === 0) {
    return (
      <div className="border border-[#E2E8F0] rounded-xl p-8 text-center">
        <p className="text-[13px] text-[#94A3B8] italic">No data to display</p>
        <p className="text-[11px] text-[#CBD5E1] mt-1">This file appears to contain mostly empty cells or a template structure</p>
      </div>
    );
  }

  return (
    <div className="border border-[#E2E8F0] rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="bg-[#1E293B]">
              {headers.map((h, i) => (
                <th key={i} className="px-3 py-2.5 text-left font-semibold text-white text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nonEmptyRows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'}>
                {row.map((cell, ci) => {
                  const { display, isEmpty } = formatCellValue(cell);
                  return (
                    <td
                      key={ci}
                      className={`px-3 py-1.5 font-mono whitespace-nowrap ${isEmpty ? 'text-[#CBD5E1] italic' : 'text-[#334155]'}`}
                    >
                      {display}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataPreviewTab;
